import { Card, Flex, Progress, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  CaseInfo,
  SalesCall1LeadContactInfo,
  PreSalesCancellationReason,
  PreSalesQuestion,
  PreSalesQuestionnaireContext,
  Slot,
} from '../../../dto/model';
import { strings } from '../../../lang';
import {
  getPreSalesQuestionnaireProgress,
} from './pre-sales-questionnaire-utils';
import {
  PreSalesQuestionnaire,
  PreSalesQuestionnaireSnapshot,
} from './pre-sales-questionnaire';
import type { PreSalesCancelledHandler, PreSalesZeegBookingHandler } from './pre-sales-questionnaire-types';
import type { BookingLaneReasonResult } from './pre-sales-booking-lane-reason-modal';
import type { PreSalesCustomerInfoPatch } from './pre-sales-questionnaire';
import { PreSalesHeatLoadCard } from './pre-sales-heat-load-card';
import { PreSalesProductOverview } from './pre-sales-product-overview';
import {
  formatPreSalesCustomerName,
  PreSalesDealInfoCard,
} from './pre-sales-deal-info-card';

type PreSalesBookingWorkspaceProps = {
  caseInfo: CaseInfo;
  bookZeegSlot?: PreSalesZeegBookingHandler;
  hasPendingZeegBooking?: () => boolean;
  bookedZeegSlot?: Slot | null;
  isLocked?: boolean;
  confirmation?: ReactNode;
  onBusyChange?: (busy: boolean) => void;
  onProductCancelled?: (reasons: string[]) => Promise<void>;
  context?: PreSalesQuestionnaireContext;
  questions: PreSalesQuestion[];
  isAdCase?: boolean;
  contactEditDisabled?: boolean;
  onContactSaved?: (contact: SalesCall1LeadContactInfo) => void;
  actions?: ReactNode;
  footerActions?: ReactNode;
  /** Content rendered at the top of the right-hand column (e.g. earlier not-booked notes for unified AD cases). */
  sideHeader?: ReactNode;
  /** Content rendered at the bottom of the right-hand column (e.g. lost reasons for unified AD cases). */
  sideActions?: ReactNode;
  /** Called when a booking succeeds (Zeeg video or field sales). Stops recording as Booked. */
  onBooked?: (slot?: Slot, lane?: string, reason?: BookingLaneReasonResult) => void | Promise<void>;
  /** Called when the presales form is submitted as a cancellation. Stops recording as ReachedButNotBooked. */
  onCancelled?: PreSalesCancelledHandler;
  onCustomerInfoChange: (patch: PreSalesCustomerInfoPatch) => void;
  /** Called when the questionnaire is first rendered and when the first qualification has settled. */
  onLoadMilestone?: (milestone: 'questionnaire' | 'qualification') => void;
};

const { Title, Text } = Typography;

export const PreSalesBookingWorkspace = ({
  caseInfo,
  context,
  questions,
  isAdCase,
  contactEditDisabled,
  onContactSaved,
  actions,
  footerActions,
  sideHeader,
  sideActions,
  bookZeegSlot,
  hasPendingZeegBooking,
  bookedZeegSlot,
  isLocked,
  confirmation,
  onBusyChange,
  onProductCancelled,
  onBooked,
  onCancelled,
  onCustomerInfoChange,
  onLoadMilestone,
}: PreSalesBookingWorkspaceProps) => {
  const [snapshot, setSnapshot] = useState<PreSalesQuestionnaireSnapshot | null>(null);
  const [lastSettledCancellationReasons, setLastSettledCancellationReasons] = useState<
    PreSalesCancellationReason[]
  >([]);
  const progress = useMemo(
    () => getPreSalesQuestionnaireProgress(
      snapshot?.visibleQuestions ?? [],
      snapshot?.answers ?? {},
    ),
    [snapshot],
  );
  const progressLabel = strings.preSalesQuestionnaire.questionnaireProgressSummary(
    progress.answered,
    progress.total,
    progress.percent,
  );
  const evaluationStatus = snapshot?.cancellationEvaluationStatus ?? 'idle';
  const cancellationReasons = evaluationStatus === 'success'
    ? snapshot?.cancellationReasons ?? []
    : lastSettledCancellationReasons;

  useEffect(() => {
    if (snapshot?.cancellationEvaluationStatus === 'success') {
      setLastSettledCancellationReasons(snapshot.cancellationReasons);
    }
  }, [snapshot]);

  useEffect(() => {
    if (!onLoadMilestone || !snapshot || snapshot.visibleQuestions.length === 0) return;
    onLoadMilestone('questionnaire');
    if (evaluationStatus === 'success' || evaluationStatus === 'error') onLoadMilestone('qualification');
  }, [onLoadMilestone, snapshot, evaluationStatus]);

  useEffect(() => {
    setLastSettledCancellationReasons([]);
  }, [caseInfo.internalId, context?.hpOrderNumber]);

  return (
    <Flex gap={16} className="bp-presales-booking-workspace">
      <Flex vertical gap={16} className="bp-presales-booking-workspace_area">
        <Card>
          <Title level={4} style={{ marginTop: 0 }}>
            {formatPreSalesCustomerName(caseInfo)}
          </Title>
          <Flex justify="space-between" gap={16}>
            <Text type="secondary">{strings.preSalesQuestionnaire.questionnaireProgress} </Text>
            <Text type="secondary">{progressLabel}</Text>
          </Flex>
          <Progress
            aria-label={progressLabel}
            percent={progress.percent}
            showInfo={false}
            size="small"
          />
          {actions}
        </Card>

        <PreSalesQuestionnaire
          bookZeegSlot={bookZeegSlot}
          hasPendingZeegBooking={hasPendingZeegBooking}
          bookedZeegSlot={bookedZeegSlot}
          isLocked={isLocked}
          confirmation={confirmation}
          onBusyChange={onBusyChange}
          onProductCancelled={onProductCancelled}
          unifiedFlow={isAdCase}
          questions={questions}
          isAdCase={isAdCase}
          footerActions={footerActions}
          context={context}
          zipCode={caseInfo.zipcode}
          customer={{
            firstName: caseInfo.firstName,
            lastName: caseInfo.lastName,
            email: caseInfo.email,
            customerSource: context?.customerSource,
          }}
          onCustomerInfoChange={onCustomerInfoChange}
          onStateChange={setSnapshot}
          onBooked={onBooked}
          onCancelled={onCancelled}
        />
      </Flex>

      <Flex vertical gap={16} className="bp-presales-booking-workspace_area">
        {sideHeader}
        <PreSalesDealInfoCard caseInfo={caseInfo} context={context} isAdCase={isAdCase}
          contactEditDisabled={contactEditDisabled} onContactSaved={onContactSaved} onContactBusyChange={onBusyChange} />
        <PreSalesProductOverview
          unifiedFlow={isAdCase}
          qualification={snapshot?.qualification}
          answers={snapshot?.answers ?? {}}
          visibleQuestions={snapshot?.visibleQuestions ?? []}
          cancellationReasons={cancellationReasons}
          evaluationStatus={evaluationStatus}
        />
        <PreSalesHeatLoadCard
          answers={snapshot?.answers ?? {}}
          visibleQuestions={snapshot?.visibleQuestions ?? []}
          cancellationReasons={cancellationReasons}
        />
        {sideActions}
      </Flex>
    </Flex>
  );
};
